import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PortfolioHealthCard, AbstractDashboard, MaintenanceCard } from "@/components/glimpses/Glimpses";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay, ease: EASE }}>
      {children}
    </motion.span>
  </span>
);

const Fade = ({ children, delay, className = "" }) => (
  <motion.div className={className} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay, ease: EASE }}>
    {children}
  </motion.div>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, -28]);

  return (
    <section className="relative overflow-hidden bg-white" data-testid="hero-section">
      <div className="container-x grid items-center gap-12 pb-16 pt-12 sm:pt-16 lg:grid-cols-12 lg:gap-10 lg:pb-24 lg:pt-24">
        <div className="lg:col-span-6">
          <Fade delay={0.05}>
            <p className="eyebrow" data-testid="hero-eyebrow">Property Intelligence &amp; Management Platform</p>
          </Fade>
          <h1 className="mt-5 font-display text-[2.1rem] font-extrabold leading-[1.05] tracking-tighter text-navy-ink sm:text-5xl lg:text-6xl" data-testid="hero-title">
            <Line delay={0.15}>Property management</Line>
            <Line delay={0.3} className="text-navy">that thinks.</Line>
          </h1>
          <Fade delay={0.55}>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate2 sm:mt-6 lg:text-lg" data-testid="hero-subcopy">
              One intelligent platform to manage operations, understand performance and make better decisions across your property portfolio.
            </p>
          </Fade>
          <Fade delay={0.7} className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <Link to="/demo" data-testid="hero-book-demo-button" className="btn-primary">
              Book a Demo <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/platform" data-testid="hero-explore-platform-button" className="btn-secondary">
              Explore ANFORD
            </Link>
          </Fade>
        </div>

        <motion.div className="relative lg:col-span-6" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.8, ease: EASE }} data-testid="hero-composition">
          <motion.div style={{ y }} className="relative mx-auto max-w-xl">
            <AbstractDashboard />
            <div className="absolute inset-x-4 -bottom-6 sm:inset-x-auto sm:right-6 sm:w-[300px] sm:-bottom-4 lg:right-8">
              <PortfolioHealthCard />
            </div>
            <div className="absolute -left-2 top-1/3 hidden w-[210px] sm:block lg:-left-6">
              <MaintenanceCard />
            </div>
          </motion.div>
          <p className="mt-12 text-center text-[11px] text-slate2/70 sm:mt-10">Illustrative interface with sample data.</p>
        </motion.div>
      </div>
    </section>
  );
};
